import { h } from 'preact'
import { useState, useRef, useEffect, useCallback } from 'preact/hooks'
import PropTypes from 'prop-types'

import classNames from 'lib/classNames'
import Icon from 'components/Icon'

/**
 * Pre-join lobby shown before entering a video call.
 * Shows: meeting info, camera preview, mic level, display name input, join button.
 */
export default function JoinPage({
  meetingName,
  hostName,
  callType,
  participantCount,
  displayName: initialDisplayName,
  onJoin,
  isJoining,
  error,
}) {
  const [displayName, setDisplayName] = useState(initialDisplayName || '')
  const [isCameraOn, setIsCameraOn] = useState(callType === 'video')
  const [isMicOn, setIsMicOn] = useState(true)
  const [cameraStream, setCameraStream] = useState(null)
  const [micLevel, setMicLevel] = useState(0)
  const videoPreviewRef = useRef(null)
  const audioContextRef = useRef(null)
  const analyserRef = useRef(null)
  const micStreamRef = useRef(null)
  const animFrameRef = useRef(null)

  useEffect(() => {
    if (!isCameraOn || callType !== 'video') {
      if (cameraStream) {
        cameraStream.getTracks().forEach(t => t.stop())
        setCameraStream(null)
      }
      return
    }
    let cancelled = false
    navigator.mediaDevices.getUserMedia({ video: true, audio: false })
      .then(stream => {
        if (cancelled) {
          stream.getTracks().forEach(t => t.stop())
          return
        }
        setCameraStream(stream)
      })
      .catch(() => {
        setIsCameraOn(false)
      })
    return () => {
      cancelled = true
    }
  }, [isCameraOn])

  useEffect(() => {
    const el = videoPreviewRef.current
    if (!el) return
    if (cameraStream) {
      el.srcObject = cameraStream
    } else {
      el.srcObject = null
    }
  }, [cameraStream])

  useEffect(() => {
    if (!isMicOn) {
      if (micStreamRef.current) {
        micStreamRef.current.getTracks().forEach(t => t.stop())
        micStreamRef.current = null
      }
      setMicLevel(0)
      return
    }
    let cancelled = false
    navigator.mediaDevices.getUserMedia({ audio: true, video: false })
      .then(stream => {
        if (cancelled) {
          stream.getTracks().forEach(t => t.stop())
          return
        }
        micStreamRef.current = stream
        const audioCtx = new (window.AudioContext || window.webkitAudioContext)()
        audioContextRef.current = audioCtx
        const analyser = audioCtx.createAnalyser()
        analyser.fftSize = 256
        analyserRef.current = analyser
        const source = audioCtx.createMediaStreamSource(stream)
        source.connect(analyser)

        const dataArray = new Uint8Array(analyser.frequencyBinCount)
        const tick = () => {
          analyser.getByteFrequencyData(dataArray)
          const avg = dataArray.reduce((a, b) => a + b, 0) / dataArray.length
          setMicLevel(Math.min(100, Math.round((avg / 128) * 100)))
          animFrameRef.current = requestAnimationFrame(tick)
        }
        tick()
      })
      .catch(() => {
        setIsMicOn(false)
      })
    return () => {
      cancelled = true
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current)
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(() => {})
        audioContextRef.current = null
      }
    }
  }, [isMicOn])

  useEffect(() => {
    return () => {
      if (cameraStream) cameraStream.getTracks().forEach(t => t.stop())
      if (micStreamRef.current) micStreamRef.current.getTracks().forEach(t => t.stop())
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current)
      if (audioContextRef.current) audioContextRef.current.close().catch(() => {})
    }
  }, [])

  const handleJoin = useCallback(() => {
    if (cameraStream) cameraStream.getTracks().forEach(t => t.stop())
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach(t => t.stop())
      micStreamRef.current = null
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {})
      audioContextRef.current = null
    }
    onJoin({
      displayName: displayName.trim() || 'Guest',
      cameraOn: isCameraOn,
      micOn: isMicOn,
    })
  }, [onJoin, displayName, isCameraOn, isMicOn, cameraStream])

  const toggleCamera = useCallback(() => setIsCameraOn(v => !v), [])
  const toggleMic = useCallback(() => setIsMicOn(v => !v), [])

  const now = new Date()
  const timeStr = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })

  return (
    <div className="JoinPage">
      <div className="JoinPage-card" style={{ overflow: 'hidden' }}>
        {/* Meeting info */}
        <div className="JoinPage-info">
          <h2 className="JoinPage-meetingName">
            <Icon type={callType === 'video' ? 'video' : 'audio'} size="md" />
            {' '}{meetingName || (callType === 'video' ? 'Video Call' : 'Audio Call')}
          </h2>
          {hostName && (
            <div className="JoinPage-detail">
              <Icon type="user-list" size="sm" />
              <span>Hosted by {hostName}</span>
            </div>
          )}
          <div className="JoinPage-detail">
            <Icon type="connectionStatus" size="sm" />
            <span>{timeStr}</span>
          </div>
          {participantCount > 0 && (
            <div className="JoinPage-detail">
              <Icon type="user-list" size="sm" />
              <span>{participantCount} participant{participantCount !== 1 ? 's' : ''} in call</span>
            </div>
          )}
        </div>

        {/* Camera preview */}
        <div className="JoinPage-preview">
          {isCameraOn ? (
            <video
              ref={videoPreviewRef}
              className="JoinPage-videoPreview"
              autoPlay
              playsInline
              muted
              style={{ transform: 'scaleX(-1)' }}
            />
          ) : (
            <div className="JoinPage-noCamera">
              <Icon type="disabledVideo" size="hg" />
              <span>Camera off</span>
            </div>
          )}

          {/* Preview controls */}
          <div className="JoinPage-previewControls">
            <button
              className={classNames('JoinPage-previewBtn', { off: !isCameraOn })}
              onClick={toggleCamera}
              title={isCameraOn ? 'Turn off camera' : 'Turn on camera'}
            >
              <Icon type={isCameraOn ? 'video' : 'disabledVideo'} size="sm" />
            </button>
            <button
              className={classNames('JoinPage-previewBtn', { off: !isMicOn })}
              onClick={toggleMic}
              title={isMicOn ? 'Mute microphone' : 'Unmute microphone'}
            >
              <Icon type={isMicOn ? 'audio' : 'audioOff'} size="sm" />
            </button>
          </div>
        </div>

        {/* Mic level bar */}
        {isMicOn && (
          <div className="JoinPage-micLevel">
            <Icon type="audio" size="sm" />
            <div className="JoinPage-micLevelTrack">
              <div
                className="JoinPage-micLevelFill"
                style={{ width: micLevel + '%' }}
              />
            </div>
          </div>
        )}

        {/* Display name input */}
        <div className="JoinPage-nameInput">
          <label className="JoinPage-label" htmlFor="join-display-name">
            Your display name
          </label>
          <input
            id="join-display-name"
            className="JoinPage-input"
            type="text"
            placeholder="Enter your name..."
            value={displayName}
            onInput={e => setDisplayName(e.target.value)}
            maxLength={50}
          />
        </div>

        {/* Error */}
        {error && (
          <div className="JoinPage-error">
            <Icon type="close" size="sm" />
            {' '}{error}
          </div>
        )}

        {/* Join button */}
        <button
          className="JoinPage-joinBtn"
          onClick={handleJoin}
          disabled={isJoining || !displayName.trim()}
        >
          {isJoining ? 'Joining...' : 'Join Call'}
        </button>
      </div>
    </div>
  )
}

JoinPage.propTypes = {
  meetingName: PropTypes.string,
  hostName: PropTypes.string,
  callType: PropTypes.string,
  participantCount: PropTypes.number,
  displayName: PropTypes.string,
  onJoin: PropTypes.func.isRequired,
  isJoining: PropTypes.bool,
  error: PropTypes.string,
}
